import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import Heading from 'LuxKit/Typography/Heading'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextButton from 'LuxKit/Button/TextButton'
import StickyPrompt from 'components/Common/StickyPromptCard/StickyPrompt'
import { mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'

const AccessWrapper = styled.div`
  position: relative;
  z-index: ${zIndex.promoDisplayConfig};
`

const CloseButton = styled(IconButton)`
  position: absolute;
  right: ${rem(20)};
  top: ${rem(20)};
`

const AccessPrompt = styled(StickyPrompt)`
  padding: ${rem(24)};

  ${mediaQueryUp.tablet} {
    width: ${rem(400)};
  }
`

const PromoCodeContainer = styled.div`
  padding: ${rem(8)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.six};
  width: 100%;
  height: min-content;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

interface PromoDisplayModelProps {
  show: boolean
  promptRef: React.RefObject<HTMLDivElement | null>
  HeaderIcon: React.ReactNode
  codeName?: string
  expiresAt?: string
  bodyText?: string
  headerText?: string
  displayMode?: string
  isCopied: boolean
  onClose: () => void
  onClickCopyIcon: () => void
}

function PromoDisplayModel({
  show,
  promptRef,
  HeaderIcon,
  codeName,
  expiresAt,
  bodyText,
  headerText,
  displayMode,
  isCopied,
  onClose,
  onClickCopyIcon,
}: PromoDisplayModelProps) {
  const intl = useIntl()

  const copyCodeLabel = intl.formatMessage({
    id: '08ORxIinYpGL7pOfbJoX',
    defaultMessage: 'Copy this code',
    description: 'Button text to copy promo code to clipboard for promotions',
  })
  const dismissLabel = intl.formatMessage({
    id: 'g0nLFnS1XCTxfcjeRcLK',
    defaultMessage: 'I do not want to see this again.',
    description: 'Button text to permanently dismiss the promo display prompt',
  })

  return (
    <AccessWrapper>
      <AccessPrompt show={show} sticky={false} ref={promptRef}>
        <CloseButton
          kind="tertiary"
          shape="square"
          onClick={onClose}
        >
          <LineTimesIcon />
        </CloseButton>
        <Group direction="vertical" gap={16} fullWidth>
          <Group direction="vertical" gap={16} horizontalAlign="center">
            {HeaderIcon}
            <Heading variant="heading3">{headerText}</Heading>
          </Group>
          <BodyTextBlock variant="medium">{bodyText}</BodyTextBlock>
          {expiresAt && (
            <BodyTextBlock variant="medium">
              {intl.formatMessage(
                {
                  id: 'jc4g9XanF8xMRt3bCxUJ',
                  defaultMessage: 'This promotion code will expire on {expiryDate}',
                  description: 'Expiry notice for a promotional code in the promo display prompt',
                },
                { expiryDate: (new Date(expiresAt)).toString() },
              )}
            </BodyTextBlock>
          )}
          <PromoCodeContainer>
            <BodyTextBlock variant="medium" weight="bold">
              {codeName}
            </BodyTextBlock>
            <IconButton
              kind="tertiary"
              size="small"
              shape="circle"
              onClick={onClickCopyIcon}
            >
              {isCopied ? <LineCheckIcon /> : <LineCopyIcon />}
            </IconButton>
          </PromoCodeContainer>
          <TextButton kind="primary" onClick={onClickCopyIcon}>
            {copyCodeLabel}
          </TextButton>
          {displayMode === 'appear_once' && (
            <TextButton kind="secondary" onClick={onClose}>
              {dismissLabel}
            </TextButton>
          )}
        </Group>
      </AccessPrompt>
    </AccessWrapper>
  )
}

export default React.memo(PromoDisplayModel)
